import React, { useMemo, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { DateTime } from 'luxon'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Avatar from '@material-ui/core/Avatar'
import { makeStyles } from '@material-ui/core/styles'
import { startOfUsWeek } from '../schedule-date-ranges'

const HOUR_PX = 48
const TIME_COL_WIDTH = 52
const DEFAULT_START_MIN = 6 * 60
const DEFAULT_END_MIN = 21 * 60

function isToday(day) {
  return day.hasSame(DateTime.local(), 'day')
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    background: theme.palette.common.white,
    flex: 1,
    minHeight: 0,
    minWidth: 0,
  },
  headerRow: {
    display: 'flex',
    flexShrink: 0,
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.grey[50],
  },
  timeColHeader: {
    width: TIME_COL_WIDTH,
    minWidth: TIME_COL_WIDTH,
    flexShrink: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    background: theme.palette.grey[50],
  },
  dayHeader: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderRight: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0.5, 0.5, 0.75),
    '&:last-child': { borderRight: 'none' },
  },
  dayLabel: {
    textAlign: 'center',
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    letterSpacing: 0.3,
  },
  dayLabelToday: {
    color: theme.palette.primary.main,
  },
  dayDateChip: {
    width: 28,
    height: 28,
    fontSize: 13,
    fontWeight: 600,
    margin: '2px auto 0',
    background: 'transparent',
    color: theme.palette.text.primary,
  },
  dayDateChipToday: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  scrollArea: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  gridBody: {
    display: 'flex',
    width: '100%',
  },
  timeCol: {
    width: TIME_COL_WIDTH,
    minWidth: TIME_COL_WIDTH,
    flexShrink: 0,
    background: theme.palette.common.white,
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  hourLabel: {
    height: HOUR_PX,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    paddingRight: theme.spacing(0.75),
    paddingTop: 2,
    fontSize: 10,
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums',
    boxSizing: 'border-box',
  },
  dayColumns: {
    display: 'flex',
    flex: 1,
    minWidth: 0,
  },
  dayColumn: {
    flex: 1,
    minWidth: 0,
    position: 'relative',
    borderRight: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderRight: 'none' },
  },
  hourLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    pointerEvents: 'none',
  },
  todayCol: {
    background: 'rgba(0, 117, 202, 0.03)',
  },
}))

/**
 * Lightweight week/day grid — headers and hour lines only. Used while schedule
 * data loads so navigation never mounts the full event calendar.
 */
function AcademyCalendarShell({ anchorDate, isSingleDay }) {
  const classes = useStyles()
  const scrollRef = useRef(null)

  const days = useMemo(() => {
    if (isSingleDay) return [anchorDate.startOf('day')]
    const w0 = startOfUsWeek(anchorDate)
    return Array.from({ length: 7 }, (_, i) => w0.plus({ days: i }))
  }, [anchorDate, isSingleDay])

  const spanMin = DEFAULT_END_MIN - DEFAULT_START_MIN
  const hourSlotCount = Math.max(1, Math.round(spanMin / 60))
  const gridHeightPx = (spanMin / 60) * HOUR_PX

  const hourLabelHours = useMemo(
    () =>
      Array.from(
        { length: hourSlotCount },
        (_, i) => DEFAULT_START_MIN / 60 + i,
      ),
    [hourSlotCount],
  )

  useEffect(() => {
    if (!scrollRef.current) return
    const scrollTo8am = ((8 * 60 - DEFAULT_START_MIN) / 60) * HOUR_PX
    scrollRef.current.scrollTop = Math.max(0, scrollTo8am - 40)
  }, [anchorDate, isSingleDay])

  return (
    <Box className={classes.root}>
      <Box className={classes.headerRow}>
        <Box className={classes.timeColHeader} />
        {days.map(day => {
          const today = isToday(day)
          const dow = isSingleDay ? day.toFormat('cccc') : day.toFormat('ccc')
          return (
            <Box key={day.toISODate()} className={classes.dayHeader}>
              <Typography
                className={`${classes.dayLabel} ${
                  today ? classes.dayLabelToday : ''
                }`}
                variant="caption"
              >
                {dow}
              </Typography>
              <Avatar
                className={`${classes.dayDateChip} ${
                  today ? classes.dayDateChipToday : ''
                }`}
              >
                {day.day}
              </Avatar>
            </Box>
          )
        })}
      </Box>

      <Box ref={scrollRef} className={classes.scrollArea}>
        <Box className={classes.gridBody}>
          <Box className={classes.timeCol} style={{ height: gridHeightPx }}>
            {hourLabelHours.map(h => {
              const hourInt = Math.min(23, Math.max(0, Math.floor(h)))
              return (
                <Box key={`h-${h}`} className={classes.hourLabel}>
                  {DateTime.fromObject({
                    year: 2000,
                    month: 1,
                    day: 1,
                    hour: hourInt,
                    minute: 0,
                  }).toFormat('h a')}
                </Box>
              )
            })}
          </Box>

          <Box className={classes.dayColumns}>
            {days.map(day => (
              <Box
                key={day.toISODate()}
                className={`${classes.dayColumn} ${
                  isToday(day) ? classes.todayCol : ''
                }`}
                style={{ height: gridHeightPx }}
              >
                {Array.from({ length: hourSlotCount }, (_, i) => (
                  <Box
                    key={i}
                    className={classes.hourLine}
                    style={{ top: i * HOUR_PX }}
                  />
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

AcademyCalendarShell.propTypes = {
  anchorDate: PropTypes.object.isRequired,
  isSingleDay: PropTypes.bool,
}

AcademyCalendarShell.defaultProps = {
  isSingleDay: false,
}

export default AcademyCalendarShell
